import { apiClient } from './client';
import type { LoginRequest, LoginResponse } from '../types/api';
import type { User } from '../types/entities';

export const authApi = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // Auth endpoint returns data directly, not wrapped in ApiResponse
    const response = await apiClient.instance.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  },

  async logout(): Promise<void> {
    try {
      await apiClient.instance.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed, continuing with client-side logout:', error);
    }
  },

  async me(): Promise<User> {
    // Me endpoint returns data directly, not wrapped in ApiResponse
    const response = await apiClient.instance.get<User>('/auth/me');
    return response.data;
  },

  async getManagers(): Promise<User[]> {
    // Managers endpoint returns data directly, not wrapped in ApiResponse
    const response = await apiClient.instance.get<User[]>('/auth/managers');
    return response.data;
  },

  async register(data: { email: string; password: string; role: string }): Promise<User> {
    // Register endpoint returns data directly, not wrapped in ApiResponse
    const response = await apiClient.instance.post<User>('/auth/register', data);
    return response.data;
  },

  async getAllUsers(): Promise<User[]> {
    const response = await apiClient.instance.get<User[]>('/auth/users');
    return response.data;
  },

  async resetUserPassword(userId: string): Promise<{ message: string; temporary_password: string }> {
    const response = await apiClient.instance.post(`/auth/users/${userId}/reset-password`);
    return response.data;
  },

  async deleteUser(userId: string): Promise<{ message: string }> {
    const response = await apiClient.instance.delete(`/auth/users/${userId}`);
    return response.data;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await apiClient.instance.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },

  async updateUser(userId: string, data: { 
    email?: string; 
    role?: string; 
    first_name?: string | null; 
    last_name?: string | null; 
    display_name?: string | null; 
  }): Promise<User> {
    const response = await apiClient.instance.put<User>(`/auth/users/${userId}`, data);
    return response.data;
  },

  // Microsoft authentication - redirect to Microsoft login
  microsoftLogin(): void {
    window.location.href = `${import.meta.env.VITE_API_URL || '/api'}/auth/ms/login`;
  },
};